import { z } from "zod";
export const OPERATIONAL_EXERCISE_KINDS = [
    "disaster_recovery",
    "security_incident_tabletop",
    "penetration_test",
];
export const OPERATIONAL_EXERCISE_OUTCOMES = ["passed", "failed"];
export const OPERATIONAL_CADENCE_STATES = [
    "current",
    "due_soon",
    "overdue",
    "never_run",
];
const dateTimeSchema = z.iso.datetime({ offset: true });
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/u);
const positiveGenerationSchema = z.string().regex(/^[1-9]\d*$/u);
const boundedTextSchema = z.string().trim().min(1).max(500);
export const operationalEvidenceObjectSchema = z
    .object({
    generation: positiveGenerationSchema,
    sha256: sha256Schema,
    uri: z
        .string()
        .trim()
        .regex(/^gs:\/\/[a-z0-9][a-z0-9._-]{1,221}\/[^\s?#]{1,900}$/u),
})
    .strict();
export const operationalFindingSchema = z
    .object({
    dueAt: dateTimeSchema.nullable(),
    owner: z.string().trim().min(1).max(200),
    resolvedAt: dateTimeSchema.nullable(),
    severity: z.enum(["critical", "high", "medium", "low"]),
    status: z.enum(["open", "resolved"]),
    summary: boundedTextSchema,
})
    .strict()
    .superRefine((finding, context) => {
    if (finding.status === "resolved" && finding.resolvedAt === null) {
        context.addIssue({
            code: "custom",
            message: "Resolved findings require resolvedAt",
            path: ["resolvedAt"],
        });
    }
    if (finding.status === "open" && finding.resolvedAt !== null) {
        context.addIssue({
            code: "custom",
            message: "Open findings cannot have resolvedAt",
            path: ["resolvedAt"],
        });
    }
});
const recoverySampleSchema = z
    .object({
    actualSha256: sha256Schema,
    expectedSha256: sha256Schema,
    generation: positiveGenerationSchema,
    objectUri: z
        .string()
        .trim()
        .regex(/^gs:\/\/[a-z0-9][a-z0-9._-]{1,221}\/[^\s?#]{1,900}$/u),
    purpose: z.enum(["audit_anchor", "verification_evidence"]),
    retentionVerified: z.boolean(),
    secondaryLocationReadVerified: z.boolean(),
})
    .strict();
const disasterRecoveryChecksSchema = z
    .object({
    auditChainVerified: z.boolean(),
    databaseRestored: z.boolean(),
    evidenceRetentionVerified: z.boolean(),
    migrationsVerified: z.boolean(),
    playerContinuityVerified: z.boolean(),
    readinessVerified: z.boolean(),
    rowLevelSecurityVerified: z.boolean(),
    tenantIsolationVerified: z.boolean(),
})
    .strict();
export const disasterRecoveryEvidenceSchema = z
    .object({
    checks: disasterRecoveryChecksSchema,
    completedAt: dateTimeSchema,
    databaseBackupId: z.string().trim().min(1).max(300),
    exerciseId: z.string().uuid(),
    findings: z.array(operationalFindingSchema).max(100),
    kind: z.literal("disaster_recovery"),
    recoveryPointAt: dateTimeSchema,
    restoredAt: dateTimeSchema,
    restoredInstanceRef: z.string().trim().min(1).max(300),
    rpoMinutes: z.number().int().nonnegative().max(10_080),
    rtoMinutes: z.number().int().nonnegative().max(10_080),
    schemaVersion: z.literal(1),
    startedAt: dateTimeSchema,
    wormSamples: z.array(recoverySampleSchema).min(2).max(20),
})
    .strict()
    .superRefine((evidence, context) => {
    const startedAt = Date.parse(evidence.startedAt);
    const completedAt = Date.parse(evidence.completedAt);
    const restoredAt = Date.parse(evidence.restoredAt);
    const recoveryPointAt = Date.parse(evidence.recoveryPointAt);
    if (recoveryPointAt > startedAt ||
        restoredAt < startedAt ||
        completedAt < restoredAt) {
        context.addIssue({ code: "custom", message: "Recovery timestamps are out of order" });
    }
    const expectedRpo = Math.ceil((startedAt - recoveryPointAt) / 60_000);
    const expectedRto = Math.ceil((restoredAt - startedAt) / 60_000);
    if (Math.abs(expectedRpo - evidence.rpoMinutes) > 1) {
        context.addIssue({ code: "custom", message: "rpoMinutes does not match timestamps", path: ["rpoMinutes"] });
    }
    if (Math.abs(expectedRto - evidence.rtoMinutes) > 1) {
        context.addIssue({ code: "custom", message: "rtoMinutes does not match timestamps", path: ["rtoMinutes"] });
    }
    const purposes = new Set(evidence.wormSamples.map((sample) => sample.purpose));
    for (const purpose of ["audit_anchor", "verification_evidence"]) {
        if (!purposes.has(purpose)) {
            context.addIssue({ code: "custom", message: `Missing ${purpose} sample`, path: ["wormSamples"] });
        }
    }
});
const incidentTabletopChecksSchema = z
    .object({
    actionsAudited: z.boolean(),
    containmentExercised: z.boolean(),
    eradicationExercised: z.boolean(),
    evidencePreserved: z.boolean(),
    notificationPathExercised: z.boolean(),
    recoveryExercised: z.boolean(),
    rolesAssigned: z.boolean(),
    severityClassified: z.boolean(),
    tenantImpactAssessed: z.boolean(),
})
    .strict();
export const securityIncidentTabletopEvidenceSchema = z
    .object({
    checks: incidentTabletopChecksSchema,
    completedAt: dateTimeSchema,
    exerciseId: z.string().uuid(),
    findings: z.array(operationalFindingSchema).max(100),
    kind: z.literal("security_incident_tabletop"),
    scenario: z.enum([
        "credential_compromise",
        "device_fleet_compromise",
        "provider_outage",
        "ransomware",
        "tenant_data_exposure",
    ]),
    schemaVersion: z.literal(1),
    startedAt: dateTimeSchema,
    tenantNotificationMinutes: z.number().int().nonnegative().max(10_080).nullable(),
    tenantNotificationRequired: z.boolean(),
})
    .strict()
    .superRefine((evidence, context) => {
    if (Date.parse(evidence.completedAt) < Date.parse(evidence.startedAt)) {
        context.addIssue({ code: "custom", message: "Tabletop timestamps are out of order" });
    }
    if (evidence.tenantNotificationRequired !==
        (evidence.tenantNotificationMinutes !== null)) {
        context.addIssue({
            code: "custom",
            message: "Notification timing must match the notification requirement",
            path: ["tenantNotificationMinutes"],
        });
    }
});
const penetrationTestScopeSchema = z
    .object({
    deviceApi: z.boolean(),
    tenantIsolation: z.boolean(),
})
    .strict();
export const penetrationTestEvidenceSchema = z
    .object({
    completedAt: dateTimeSchema,
    exerciseId: z.string().uuid(),
    findings: z
        .object({
        criticalOpen: z.number().int().nonnegative().max(10_000),
        highOpen: z.number().int().nonnegative().max(10_000),
        lowOpen: z.number().int().nonnegative().max(10_000),
        mediumOpen: z.number().int().nonnegative().max(10_000),
        slaBreaches: z.number().int().nonnegative().max(10_000),
    })
        .strict(),
    kind: z.literal("penetration_test"),
    provider: z.string().trim().min(2).max(200),
    schemaVersion: z.literal(1),
    scope: penetrationTestScopeSchema,
    startedAt: dateTimeSchema,
})
    .strict()
    .superRefine((evidence, context) => {
    if (Date.parse(evidence.completedAt) < Date.parse(evidence.startedAt)) {
        context.addIssue({ code: "custom", message: "Penetration-test timestamps are out of order" });
    }
});
export const operationalExerciseEvidenceSchema = z.discriminatedUnion("kind", [
    disasterRecoveryEvidenceSchema,
    securityIncidentTabletopEvidenceSchema,
    penetrationTestEvidenceSchema,
]);
function noOpenCriticalFindings(evidence) {
    return !evidence.findings.some((finding) => finding.severity === "critical" && finding.status === "open");
}
export function operationalExercisePassed(evidence) {
    if (evidence.kind === "disaster_recovery") {
        return (evidence.rpoMinutes <= 60 &&
            evidence.rtoMinutes <= 240 &&
            Object.values(evidence.checks).every(Boolean) &&
            evidence.wormSamples.every((sample) => sample.actualSha256 === sample.expectedSha256 &&
                sample.retentionVerified &&
                sample.secondaryLocationReadVerified) &&
            noOpenCriticalFindings(evidence));
    }
    if (evidence.kind === "security_incident_tabletop") {
        return (Object.values(evidence.checks).every(Boolean) &&
            (!evidence.tenantNotificationRequired ||
                (evidence.tenantNotificationMinutes !== null &&
                    evidence.tenantNotificationMinutes <= 72 * 60)) &&
            noOpenCriticalFindings(evidence));
    }
    return (evidence.scope.deviceApi &&
        evidence.scope.tenantIsolation &&
        evidence.findings.criticalOpen === 0 &&
        evidence.findings.slaBreaches === 0);
}
function canonicalValue(value) {
    if (Array.isArray(value))
        return value.map(canonicalValue);
    if (value !== null && typeof value === "object") {
        return Object.fromEntries(Object.entries(value)
            .sort(([left], [right]) => left < right ? -1 : left > right ? 1 : 0)
            .map(([key, entry]) => [key, canonicalValue(entry)]));
    }
    return value;
}
export function canonicalOperationalEvidence(evidence) {
    return JSON.stringify(canonicalValue(evidence));
}
export const recordOperationalExerciseCommandSchema = z
    .object({
    evidence: operationalExerciseEvidenceSchema,
    title: z.string().trim().min(3).max(200),
})
    .strict();
export const platformOperationalExerciseSchema = z
    .object({
    completedAt: dateTimeSchema,
    createdAt: dateTimeSchema,
    createdByIdentityId: z.string().uuid(),
    evidence: operationalExerciseEvidenceSchema,
    evidenceObject: operationalEvidenceObjectSchema,
    id: z.string().uuid(),
    kind: z.enum(OPERATIONAL_EXERCISE_KINDS),
    outcome: z.enum(OPERATIONAL_EXERCISE_OUTCOMES),
    startedAt: dateTimeSchema,
    title: z.string().trim().min(3).max(200),
})
    .strict();
export const platformOperationalCadenceSchema = z
    .object({
    intervalDays: z.number().int().positive(),
    kind: z.enum(OPERATIONAL_EXERCISE_KINDS),
    lastCompletedAt: dateTimeSchema.nullable(),
    nextDueAt: dateTimeSchema.nullable(),
    state: z.enum(OPERATIONAL_CADENCE_STATES),
})
    .strict();
export const platformOperationalReadinessWorkspaceSchema = z
    .object({
    cadence: z.array(platformOperationalCadenceSchema).readonly(),
    exercises: z.array(platformOperationalExerciseSchema).readonly(),
    generatedAt: dateTimeSchema,
})
    .strict();
//# sourceMappingURL=operational-readiness.js.map